import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck,  Clock, AlertTriangle, Wrench, Calendar, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { notificationService } from '../../services/notificationService';
import { useDateFormat } from '../../hooks/useDateFormat';

const NotificationBell = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const { formatRelativeTime } = useDateFormat();

  // Load unread count on mount, every 15 s, and whenever the tab regains focus
  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 15000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadUnreadCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', loadUnreadCount);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', loadUnreadCount);
    };
  }, []);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.getUnreadNotifications();
      setNotifications(response || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationService.markAsRead(id);
      setNotifications(notifications.filter(n => n.id !== id));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
    handleMarkAsRead(notification.id, { stopPropagation: () => {} });
  };

  const getNotificationIcon = (type) => {
    const iconClass = "w-5 h-5";
    switch (type) {
      case 'WORK_ORDER_CREATED':
      case 'WORK_ORDER_ASSIGNED':
      case 'WORK_ORDER_UPDATED':
      case 'WORK_ORDER_COMPLETED':
        return <Wrench className={iconClass + " text-blue-500"} />;
      case 'WORK_ORDER_OVERDUE':
      case 'PM_SCHEDULE_OVERDUE':
      case 'COMPONENT_OVERDUE':
        return <AlertTriangle className={iconClass + " text-red-500"} />;
      case 'PM_SCHEDULE_DUE':
      case 'COMPONENT_DUE':
        return <Calendar className={iconClass + " text-orange-500"} />;
      case 'SUBSCRIPTION_TRIAL_ENDING':
      case 'SUBSCRIPTION_EXPIRED':
      case 'SUBSCRIPTION_RENEWED':
        return <CreditCard className={iconClass + " text-purple-500"} />;
      default:
        return <Bell className={iconClass + " text-gray-500"} />;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">{t('nav.notifications')}</h3>
            {notifications.length > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                <CheckCheck className="w-4 h-4" />
                {t('account.markAllRead')}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Bell className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">{t('account.noNewNotifications')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="flex gap-3 p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {notification.title}
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleMarkAsRead(notification.id, e)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                      title={t('account.markAsRead')}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Link
              to="/portal/account/notifications"
              onClick={() => setIsOpen(false)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('account.viewAllNotifications')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
